<template>
  <div>
    <h1
      :style="{marginTop: 0, marginBottom: '30px'}"
      data-automation="setup-title"
    >
      Setup
    </h1>
    <p>
      Packages in this repository are installed using the
      <router-link
        v-if="cranRepoId"
        :to="{ name: 'package', params: { repoId: cranRepoId, packageName: 'BiocManager' } }"
        data-automation="setup-bioc-biocmanager"
      >BiocManager</router-link>
      <a
        v-else
        href="https://cran.r-project.org/package=BiocManager"
        target="_blank"
        rel="noopener noreferrer"
      >BiocManager</a>
      package. If you're not familiar with BiocManager, see
      <a
        href="https://bioconductor.org/install/"
        target="_blank"
        rel="noopener noreferrer"
        data-automation="setup-bioc-install-bioc"
      >Install Bioconductor</a>
      to get started.
    </p>
    <p data-automation="setup-bioc-para">
      Before you begin, check to see if BiocManager is already configured to use
      this repository. Run <code>options('BioC_mirror')</code> in an R console to
      identify where BiocManager will look for Bioconductor packages. If this
      doesn't show a URL like <code data-automation="setup-bioc-repo-url">{{ repoURL }}</code>, or if you'd like to
      change it, read on.
    </p>

    <h2 data-automation="setup-bioc-header">
      Configure the Bioconductor Repository
    </h2>
    <div class="textWithButton">
      <h3>
        <span
          class="setupCodeLabel"
          data-automation="setup-bioc-code-label"
        >Bioconductor repository setup code</span>
      </h3>
      <div>
        <button
          class="copyButton"
          data-clipboard-target="#setupBiocCode"
          data-automation="setup-bioc-copy-button"
        >
          Copy
        </button>
      </div>
    </div>
    <pre
      id="setupBiocCode"
      data-automation="setup-bioc-code"
    >options(BioC_mirror = "{{ repoURL }}")</pre>

    <p>
      We recommend adding this to your R startup file (<code>Rprofile.site</code>
      or <code>.Rprofile</code>) to maintain the configuration
      across R sessions. More information about managing R startup files is available in
      <a
        href="https://support.rstudio.com/hc/en-us/articles/360047157094-Managing-R-with-Rprofile-Renviron-Rprofile-site-Renviron-site-rsession-conf-and-repos-conf"
        target="_blank"
        rel="noopener noreferrer"
        data-automation="setup-bioc-article"
      >this article</a>.
    </p>

    <h2 data-automation="setup-cran-header">
      Using a CRAN Snapshot
    </h2>
    <p>
      For past Bioconductor releases, we recommend using a CRAN snapshot
      that provides compatible CRAN packages for that Bioconductor release.
      <span v-if="cranRepo">
        Run <code>BiocManager::version()</code> in an R console to identify your
        version of Bioconductor, then select an appropriate CRAN snapshot below.
      </span>
    </p>
    <p v-if="!cranRepo">
      An administrator can create a CRAN repository that provides CRAN snapshots
      for recent versions of Bioconductor. Instructions for doing so are
      available in the <a
        :href="quickStartDocURL"
        target="_blank"
      >admin guide</a>.
    </p>

    <div v-if="cranRepo">
      <div class="textWithButton">
        <h3>
          <span
            class="setupCodeLabel"
            data-automation="setup-cran-code-label"
          >
            CRAN repository setup code for Bioconductor {{ biocVersionLabel }}
          </span><a
            class="changeLink"
            data-automation="setup-bioc-change-link"
            href=""
            @click.prevent="handleChangeBiocVersion"
          >change</a>
        </h3>
        <div v-if="cranRepoURL">
          <button
            class="copyButton"
            data-clipboard-target="#setupCranCode"
            data-automation="setup-cran-copy-button"
          >
            Copy
          </button>
        </div>
      </div>
      <div v-if="cranRepoURL">
        <pre
          id="setupCranCode"
          data-automation="setup-cran-code"
        >options(repos = c(CRAN = "{{ cranRepoURL }}"))</pre>

        <div v-if="!binaryUsesSourceUrl && !binaryLinkDisabled">
          <div class="flexSpaceAfter">
            <p>You can also choose to use source or binary CRAN packages.</p>
            <div class="actionBar inline">
              <ActionButton
                :button-class="packagesInfoClass"
                label="info"
                data-automation="setup-info-button"
                @doIt="toggleShowPackagesInfo"
              />
            </div>
          </div>
          <div
            v-if="showPackagesInfo"
            class="toggleableInfo"
            data-automation="setup-info-text"
          >
            <p>
              Source packages work with any OS and version of R - but must be compiled, and can therefore be slow to install.
            </p>
            <p>
              Binary packages are precompiled and install quickly - but you need to specify which OS you are using. You also need to ensure
              your R environment is set up correctly. See the
              <a
                data-automation="setup-binary-admin-guide"
                :href="binariesDocURL"
                target="_blank"
              >admin guide</a>
              for more information.
            </p>
            <p>
              Note that binary packages are available for CRAN, but not Bioconductor at this time.
            </p>
          </div>

          <div class="menu showOnMobile">
            <div class="menuItems">
              <a
                id="source-url-link"
                class="menuItem"
                :class="{ active: whichPackages === PACKAGES_SOURCE }"
                data-automation="setup-repo-url-source"
                @click="setWhichPackages(PACKAGES_SOURCE)"
              >Source</a>
              <a
                class="menuItem"
                :class="{ active: whichPackages === PACKAGES_BINARY }"
                data-automation="setup-repo-url-binary"
                @click="setWhichPackages(PACKAGES_BINARY)"
              >Binary</a>
            </div>
          </div>
        </div>
        <p>
          Like the <code>BioC_mirror</code> option, you can add this to
          your R startup file (<code>Rprofile.site</code> or <code>.Rprofile</code>)
          to maintain the configuration across R sessions.
        </p>
      </div>
      <div v-else>
        <pre data-automation="setup-no-cran-snapshot">No CRAN snapshot available for Bioconductor {{ biocVersion }}</pre>
      </div>
    </div>

    <div v-if="hasSysReqs">
      <h2>Install System Prerequisites for the Repo’s Packages</h2>
      <div class="textWithButton">
        <h3>
          <span
            class="setupCodeLabel"
            data-automation="setup-sysreq-bioc-label"
          >For Bioconductor {{ biocVersionLabel }}</span><a
            class="changeLink"
            data-automation="setup-sysreq-bioc-change-link"
            href=""
            @click.prevent="handleChangeBiocVersion"
          >change</a><span
            class="setupCodeLabel"
            data-automation="setup-sysreq-os-label"
          >on {{ selectedOS.display }}</span><a
            class="changeLink"
            data-automation="setup-binary-distro-change"
            href=""
            @click.prevent="handleChangeOS"
          >change</a>
        </h3>
        <div>
          <div class="actionBar inline">
            <ActionButton
              :button-class="systemDependenciesInfoClass"
              label="info"
              data-automation="setup-sysreq-info"
              @doIt="toggleShowSystemDependenciesInfo"
            />
          </div>
          <button
            v-if="haveInstructions && !gettingSystemDependencies"
            class="copyButton"
            data-clipboard-target="#sysInstallInstructions"
            data-automation="setup-sysreq-copy"
          >
            Copy
          </button>
        </div>
      </div>

      <div
        v-if="showSystemDependenciesInfo"
        class="toggleableInfo"
      >
        <p data-automation="setup-sysreq-info-text">
          Known system prerequisites are automatically detected from the package's
          system requirements information.
          See the <a
            data-automation="package-sysreqs-link"
            :href="sysreqsDocURL"
            target="_blank"
          >admin guide</a>
          for more details on system dependency detection and its limitations.
        </p>
      </div>

      <GettingMore
        :show="gettingSystemDependencies"
        label="Getting system prerequisites"
      />
      <pre
        v-if="haveInstructions && !gettingSystemDependencies"
        style="max-height: 165px;"
      ><code
        id="sysInstallInstructions"
        data-automation="setup-sysreq-instructions"
      >{{ instructions }}</code></pre>
      <div
        v-if="!haveInstructions && !gettingSystemDependencies"
        class="toggleableInfo"
      >
        <p>No known prerequisites</p>
      </div>
    </div>

    <h2 data-automation="setup-bioc-offline-header">
      Using Packages Offline
    </h2>
    <p>
      BiocManager may require additional steps to be used in an offline environment.
      If you have a recent version of BiocManager installed, you can enable offline use
      by setting the <code>BIOCONDUCTOR_CONFIG_FILE</code> option in R:
    </p>
    <div class="textWithButton">
      <div class="setupCodeTitle">
        <span
          class="setupCodeLabel"
          data-automation="setup-offline-setup-code-label"
        >Bioconductor offline setup code</span>
      </div>
      <div>
        <button
          class="copyButton"
          data-clipboard-target="#offlineSetupCode"
          data-automation="setup-offline-setup-copy-button"
        >
          Copy
        </button>
      </div>
    </div>
    <pre
      id="offlineSetupCode"
      data-automation="setup-offline-setup-code"
    >options(BIOCONDUCTOR_CONFIG_FILE = "{{ repoURL }}/config.yaml")</pre>

    <p>
      Add this to your R startup file (<code>Rprofile.site</code>
      or <code>.Rprofile</code>) to maintain the configuration
      across R sessions. More information about managing R startup files is available in
      <a
        href="https://support.rstudio.com/hc/en-us/articles/360047157094-Managing-R-with-Rprofile-Renviron-Rprofile-site-Renviron-site-rsession-conf-and-repos-conf"
        target="_blank"
        rel="noopener noreferrer"
        data-automation="setup-bioc-offline-article"
      >this article</a>.
    </p>

    <p>
      If you have an earlier version of BiocManager installed (1.30.10 or below),
      the <code>BIOCONDUCTOR_CONFIG_FILE</code> option is not supported. Refer to the
      <a
        href="https://cran.r-project.org/web/packages/BiocManager/vignettes/BiocManager.html"
        target="_blank"
        rel="noopener noreferrer"
        data-automation="setup-bioc-offline-vignette"
      >BiocManager vignette</a> for instructions on configuring BiocManager for
      offline use. You can check your version of BiocManager by running
      <code>packageVersion("BiocManager")</code> in an R console.
    </p>

    <h2 data-automation="setup-bioc-success-header">
      Checking for Success
    </h2>
    <p data-automation="setup-bioc-success-para">
      Once you're done, run <code>BiocManager::repositories()</code> in an
      R console to check where BiocManager will look for Bioconductor and
      CRAN packages. This should show the repository URLs configured above.
      You can also list available packages using
      <code>BiocManager::available()</code>, or install a package using
      <code>BiocManager::install("BiocVersion")</code>.
    </p>

    <h2 data-automation="setup-bioc-server-admins-header">
      Instructions for RStudio Server Admins
    </h2>
    <p>
      If your organization uses RStudio Server, an administrator can configure
      the repository options for all users in a configuration file. Instructions for
      specific scenarios are available in the <a
        :href="rstudioServerDocURL"
        target="_blank"
        data-automation="setup-admin-guide"
      >admin guide</a>.
    </p>

    <ChangeBiocVersionDialog
      v-if="showChangeBiocVersionDialog"
      @closeDialog="closeChangeBiocVersionDialogDialog"
    />
    <ChangeOSDialog
      v-if="showChangeOSDialog"
      @closeDialog="closeChangeOSDialogDialog"
    />
  </div>
</template>

<script>

import { mapGetters, mapState, mapActions } from 'vuex'
import { serverPath } from '../../api/http-common'
import * as types from '../../store/mutation-types'
import GettingMore from '../GettingMore'
import ActionButton from '../ActionButton'
import ChangeBiocVersionDialog from '../dialogs/ChangeBiocVersionDialog'
import ChangeOSDialog from '../dialogs/ChangeOSDialog'

const PACKAGES_SOURCE = 'source'
const PACKAGES_BINARY = 'binary'

function printSystemRequirement (result) {
  if (!result) {
    return ''
  }
  const script = []
  result.forEach(req => {
    script.push('# ' + req.name + ' requirements:')
    // Collapse all of the instructions.
    if (req.requirements.pre_install) {
      req.requirements.pre_install.forEach(cmd => {
        script.push(cmd.command)
        script.push(cmd.script)
      })
    }
    if (req.requirements.install_scripts) {
      req.requirements.install_scripts.forEach(install => {
        script.push(install)
      })
    }
    if (req.requirements.post_install) {
      req.requirements.post_install.forEach(cmd => {
        script.push(cmd.command)
        script.push(cmd.script)
      })
    }
    script.push('')
  })
  return script.filter(s => s != null).join('\n')
}

export default {
  name: 'OverviewBioc',
  components: {
    GettingMore,
    ActionButton,
    ChangeBiocVersionDialog,
    ChangeOSDialog,
  },
  data: function () {
    return {
      PACKAGES_SOURCE: PACKAGES_SOURCE,
      PACKAGES_BINARY: PACKAGES_BINARY,
      whichPackages: PACKAGES_SOURCE,
      showPackagesInfo: false,
      showSystemDependenciesInfo: false,
      showChangeBiocVersionDialog: false,
      showChangeOSDialog: false,
    }
  },
  computed: {
    ...mapState({
      currentRepoId: state => state.repos.currentRepoId,
      allRepos: state => state.repos.allRepos,
      gettingSystemDependencies: state => state.repos.gettingRepoSystemDependencies,
      instructions: state => printSystemRequirement(state.repos.currentRepoSystemDependencies),
      selectedBiocVersion: state => state.packages.selectedBiocVersion,
      selectedOS: state => state.packages.selectedOS,
      cranRepo: state => state.status.cranRepo,
    }),
    ...mapGetters([
      'currentRepo',
    ]),
    pmServerURL: function () {
      return serverPath()
    },
    quickStartDocURL: function () {
      return `${serverPath()}__docs__/admin/getting-started/configuration/#quickstart-bioconductor-repos`
    },
    sysreqsDocURL: function () {
      return `${serverPath()}__docs__/admin/appendix/system-dependency-detection/`
    },
    binariesDocURL: function () {
      return `${serverPath()}__docs__/admin/serving-binaries/`
    },
    rstudioServerDocURL: function () {
      return `${serverPath()}__docs__/admin/rstudio-server/`
    },
    hasBinaries: function () {
      return this.selectedOS.binaries
    },
    hasSysReqs: function () {
      return this.selectedOS.sysReqs
    },
    repoURL: function () {
      return this.pmServerURL + this.currentRepo.name
    },
    cranRepoURL: function () {
      if (!this.cranRepo || !this.cranSnapshot) {
        return null
      }
      let url = this.pmServerURL + this.cranRepo
      if (this.whichPackages === PACKAGES_BINARY && this.hasBinaries && !this.binaryUsesSourceUrl) {
        url += `/__linux__/${this.selectedOS.binaryURL}`
      }
      url += `/${this.cranSnapshot}`
      return url
    },
    cranRepoId: function () {
      if (!this.cranRepo) {
        return null
      }
      const cranRepo = this.allRepos.find(repo => repo.name === this.cranRepo)
      return cranRepo ? cranRepo.id : null
    },
    biocVersion: function () {
      return this.selectedBiocVersion.bioc_version
    },
    biocVersionLabel: function () {
      const rVersion = this.selectedBiocVersion.r_version ? ` (R ${this.selectedBiocVersion.r_version})` : ''
      return this.selectedBiocVersion.bioc_version + rVersion
    },
    cranSnapshot: function () {
      return this.selectedBiocVersion.cran_snapshot
    },
    binaryUsesSourceUrl: function () {
      return this.selectedOS !== null && this.selectedOS.binaryURL === ''
    },
    binaryLinkDisabled: function () {
      return this.selectedOS !== null && this.selectedOS.binaryURL !== '' && !this.selectedOS.binaries
    },
    packagesInfoClass: function () {
      return this.showPackagesInfo ? 'toggleInfo current' : 'toggleInfo'
    },
    systemDependenciesInfoClass: function () {
      return this.showSystemDependenciesInfo ? 'toggleInfo current' : 'toggleInfo'
    },
    haveInstructions: function () {
      return this.instructions && this.instructions.length > 0
    },
  },
  watch: {
    currentRepoId: function (value) {
      this.getSystemDependencies()
    },
    selectedOS: function (newValue, oldValue) {
      this.getSystemDependencies()
    },
    selectedBiocVersion: function () {
      this.getSystemDependencies()
    },
  },
  created: function () {
    this.getSystemDependencies()
  },
  mounted: function () {
    this.$store.commit(types.SET_CURRENT_NAV, 'overview')
  },
  methods: {
    ...mapActions([
      'getRepoSystemDependencies',
    ]),
    setWhichPackages: function (which) {
      this.whichPackages = which
    },
    getSystemDependencies: function () {
      if (this.currentRepoId && this.selectedOS.sysReqs) {
        this.getRepoSystemDependencies({ repoId: this.currentRepoId, os: this.selectedOS })
      }
    },
    toggleShowPackagesInfo: function () {
      this.showPackagesInfo = !this.showPackagesInfo
    },
    toggleShowSystemDependenciesInfo: function () {
      this.showSystemDependenciesInfo = !this.showSystemDependenciesInfo
    },
    handleChangeBiocVersion: function () {
      this.showChangeBiocVersionDialog = true
    },
    closeChangeBiocVersionDialogDialog: function () {
      this.showChangeBiocVersionDialog = false
    },
    handleChangeOS: function () {
      this.showChangeOSDialog = true
    },
    closeChangeOSDialogDialog: function () {
      this.showChangeOSDialog = false
    },
  },
}

</script>

<style scoped>
  p {
    font-size: 16px;
  }

  .setupCodeTitle {
    margin-bottom: .5em;
    font-size: 1.6em;
    font-weight: 300;
  }

  .setupCodeLabel {
    padding-left: 5px;
    font-size: .7em;
  }

  .changeLink {
    font-size: 12px;
    font-weight: normal;
    text-transform: none;
    margin-left: 15px;
    margin-right: 10px;
  }
</style>
